import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { SectionHeaderType } from 'corgi-types';
import {
  alignItems,
  display,
  flexDirection,
  flexWrap,
  justifyContent,
  marginBottom,
  marginLeft,
} from 'styled-system';
import type { MarginBottomProps } from 'styled-system';

import { BodyRegularBR1, HeadlineH5 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import { useAlert } from '../hooks/useAlert';
import type { SectionHeaderProps } from '../types';
import { HEADER_TYPE_OPTIONS } from './constants';

const UnstyledHeader = styled.header<any>`
  ${display};
  ${alignItems};
  ${justifyContent};
  ${flexDirection};
  ${flexWrap};
  ${marginBottom};
  ${marginLeft};
  ${({ withBorderBottom, theme: { borderWidths, colors } }) =>
    withBorderBottom &&
    css`
      border-bottom: ${borderWidths.small} solid ${colors.sys.neutral.border.weak};
    `}
`;

const Header = (props: ComponentProps<typeof UnstyledHeader>) => (
  <UnstyledHeader display="flex" alignItems="baseline" flexWrap="wrap" {...props} />
);

const headerFullWidthProps = {
  justifyContent: ['flex-start', 'space-between'],
  flexDirection: ['column', 'row'],
};

const SectionHeading = styled(HeadlineH5)`
  overflow-wrap: anywhere;
`;

const DEFAULT_TRANSLATION_PROPS = {};

type SectionHeaderContentsProps = SectionHeaderProps & MarginBottomProps;

const SectionHeader = (props: SectionHeaderContentsProps) => {
  const { WrapperComponent } = HEADER_TYPE_OPTIONS.get(props.headerType) || {};

  if (WrapperComponent) {
    return (
      <WrapperComponent {...props}>
        {wrapperProps => <SectionHeaderContents {...wrapperProps} />}
      </WrapperComponent>
    );
  } else return <SectionHeaderContents {...props} />;
};

// eslint-disable-next-line complexity
const SectionHeaderContents = ({
  mb,
  marginBottom: marginBottomProp,
  headerType = SectionHeaderType.Default,
  heading,
  headingTranslationProps = DEFAULT_TRANSLATION_PROPS,
  subHeadingTranslationProps = DEFAULT_TRANSLATION_PROPS,
  alertTranslationProps = DEFAULT_TRANSLATION_PROPS,
  headingAs = 'h1',
  headingId,
  withBorderBottom,
  fullWidth,
}: SectionHeaderContentsProps) => {
  const { r } = useLocalization();

  const headerOptions = HEADER_TYPE_OPTIONS.get(headerType) || {};
  const alert = useAlert({ headerOptions, alertTranslationProps });
  const headingText = heading || r.t(headerOptions.heading, headingTranslationProps);

  const subHeadingTranslationKey = headerOptions.subHeading;
  const subHeading =
    subHeadingTranslationKey && r.t(subHeadingTranslationKey, subHeadingTranslationProps);

  const hasBorderBottom = withBorderBottom !== undefined ? withBorderBottom : !!subHeading;

  const marginProps = {
    mb,
    marginBottom: marginBottomProp,
  };

  return (
    <>
      <Header
        {...marginProps}
        {...(fullWidth ? headerFullWidthProps : {})}
        {...([
          SectionHeaderType.Cart,
          SectionHeaderType.MyProfile,
          SectionHeaderType.MyProfileThirdPartyConnected,
          SectionHeaderType.MyProfileThirdPartyFailed,
        ].includes(headerType)
          ? {
              ml: ['sp_12', 'sp_16', 'sp_0'],
              mb: ['sp_8', null, 'sp_0'],
            }
          : {})}
        data-corgi-id="section-header"
        withBorderBottom={hasBorderBottom}
        data-testid="section-header-container"
      >
        <SectionHeading
          m="sp_0"
          mr="sp_4"
          id={headingId}
          as={headingAs as React.ElementType}
          mb={fullWidth ? ['sp_8', 'sp_0'] : 'sp_0'}
        >
          {headingText}
        </SectionHeading>
        {subHeading && <BodyRegularBR1 as="small">{subHeading}</BodyRegularBR1>}
      </Header>
      {alert}
    </>
  );
};

export default SectionHeader;
